import {
	applyNodeChanges,
	Background,
	BackgroundVariant,
	type Connection,
	Controls,
	type Edge,
	Handle,
	MarkerType,
	MiniMap,
	type Node,
	type NodeChange,
	type NodeProps,
	Panel,
	Position,
	ReactFlow,
	ReactFlowProvider,
	useReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { LayoutGrid, Plus, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/occurrences/button'
import { cn } from '@/lib/utils'
import { newId } from '@/mocks/occurrences/store'
import type { Cause } from '@/types/occurrence'

const NODE_W = 230
const NODE_H = 104
const COL_GAP = 90
const ROW_GAP = 18

/** Layout automático em árvore (esquerda → direita): folhas em linhas sucessivas, pais centralizados. */
function autoLayout(all: Cause[], root: Cause) {
	const pos = new Map<string, { x: number; y: number }>()
	let row = 0
	const place = (node: Cause, depth: number): number => {
		const kids = all.filter((c) => c.parentId === node.id)
		const ys = kids.map((k) => place(k, depth + 1))
		const y =
			ys.length > 0
				? (ys[0] + ys[ys.length - 1]) / 2
				: row++ * (NODE_H + ROW_GAP)
		pos.set(node.id, { x: depth * (NODE_W + COL_GAP), y })
		return y
	}
	place(root, 0)
	return pos
}

const descendantsOf = (all: Cause[], id: string) => {
	const ids = new Set<string>([id])
	let grew = true
	while (grew) {
		grew = false
		for (const c of all)
			if (c.parentId && ids.has(c.parentId) && !ids.has(c.id)) {
				ids.add(c.id)
				grew = true
			}
	}
	return ids
}

interface CauseNodeData extends Record<string, unknown> {
	cause: Cause
	readOnly: boolean
	isEvent: boolean
	hasKids: boolean
	onPatch: (id: string, p: Partial<Cause>) => void
	onAdd: (parentId: string) => void
	onRemove: (id: string) => void
}
type CauseNode = Node<CauseNodeData, 'cause'>

const CauseNodeView = ({ data, selected }: NodeProps<CauseNode>) => {
	const { cause: n, readOnly, isEvent, hasKids } = data
	return (
		<div
			className={cn(
				'relative flex flex-col rounded-lg border bg-card p-1.5 shadow-sm',
				isEvent && 'border-2 border-kaefer-dark-grey',
				n.isRoot &&
					'border-2 border-kaefer-grass-green bg-kaefer-grass-green/5',
				selected && 'ring-2 ring-kaefer-sapphire-blue/50',
			)}
			style={{ width: NODE_W, height: NODE_H }}
		>
			{!isEvent && (
				<Handle
					type="target"
					position={Position.Left}
					isConnectable={!readOnly}
					className="!size-3 !border-2 !border-white !bg-kaefer-dark-grey"
				/>
			)}
			<Handle
				type="source"
				position={Position.Right}
				isConnectable={!readOnly}
				className="!size-3 !border-2 !border-white !bg-kaefer-dark-grey"
			/>
			{isEvent && (
				<span className="absolute -top-2 left-2 rounded bg-kaefer-dark-grey px-1.5 text-[10px] font-semibold text-white">
					EVENTO
				</span>
			)}
			{n.isRoot && (
				<span className="absolute -top-2 right-2 rounded bg-kaefer-grass-green px-1.5 text-[10px] font-semibold text-white">
					CAUSA-RAIZ
				</span>
			)}
			<textarea
				className="nodrag nowheel nopan min-h-0 flex-1 resize-none bg-transparent px-1 text-xs text-kaefer-dark-grey outline-none placeholder:italic placeholder:text-muted-foreground"
				disabled={readOnly}
				value={n.text}
				placeholder={
					isEvent
						? 'Descreva o evento indesejado...'
						: 'Clique para descrever...'
				}
				onChange={(e) => data.onPatch(n.id, { text: e.target.value })}
			/>
			{!readOnly && (
				<div className="nodrag flex items-center justify-between border-t pt-1">
					{isEvent ? (
						<span className="text-[10px] text-muted-foreground">Evento</span>
					) : (
						<label className="flex cursor-pointer items-center gap-1 text-[11px] font-medium text-kaefer-forest-green">
							<input
								type="checkbox"
								checked={n.isRoot}
								onChange={(e) =>
									data.onPatch(n.id, { isRoot: e.target.checked })
								}
							/>
							Causa-raiz
						</label>
					)}
					<div className="flex items-center gap-1">
						{hasKids && (
							<button
								type="button"
								title="Relação entre as causas filhas (clique para alternar)"
								onClick={() =>
									data.onPatch(n.id, {
										logic: n.logic === 'AND' ? 'OR' : 'AND',
									})
								}
								className="flex h-6 min-w-7 items-center justify-center rounded-full border-2 border-kaefer-sapphire-blue bg-white px-1 text-[10px] font-bold text-kaefer-sapphire-blue"
							>
								{n.logic === 'AND' ? 'E' : 'OU'}
							</button>
						)}
						<button
							type="button"
							title="Adicionar causa filha"
							onClick={() => data.onAdd(n.id)}
							className="flex size-6 items-center justify-center rounded-full bg-kaefer-dark-grey text-white hover:bg-kaefer-medium-grey"
						>
							<Plus className="size-3.5" />
						</button>
						{!isEvent && (
							<button
								type="button"
								title="Remover (e filhos)"
								onClick={() => data.onRemove(n.id)}
								className="flex size-6 items-center justify-center rounded-full border text-kaefer-red hover:bg-kaefer-red/10"
							>
								<Trash2 className="size-3" />
							</button>
						)}
					</div>
				</div>
			)}
		</div>
	)
}

const nodeTypes = { cause: CauseNodeView }

const round = (n: number) => Math.round(n)

const Canvas = ({
	all,
	root,
	readOnly,
	onChange,
}: {
	all: Cause[]
	root: Cause
	readOnly: boolean
	onChange: (next: Cause[]) => void
}) => {
	const { fitView } = useReactFlow()

	const build = useCallback(
		(prev: CauseNode[]): CauseNode[] => {
			const auto = autoLayout(all, root)
			const patch = (id: string, p: Partial<Cause>) =>
				onChange(all.map((c) => (c.id === id ? { ...c, ...p } : c)))
			const effective = (c: Cause) =>
				c.x !== undefined && c.y !== undefined
					? { x: c.x, y: c.y }
					: (auto.get(c.id) ?? { x: 0, y: 0 })
			const add = (parentId: string) => {
				const parent = all.find((c) => c.id === parentId)
				if (!parent) return
				const p = effective(parent)
				const kids = all.filter((c) => c.parentId === parentId).length
				onChange([
					...all,
					{
						id: newId(),
						text: '',
						parentId,
						isRoot: false,
						logic: 'OR',
						x: round(p.x + NODE_W + COL_GAP),
						y: round(p.y + kids * (NODE_H + ROW_GAP)),
					},
				])
			}
			const remove = (id: string) => {
				const ids = descendantsOf(all, id)
				onChange(all.filter((c) => !ids.has(c.id)))
			}
			return all.map((c) => {
				const old = prev.find((p) => p.id === c.id)
				return {
					...(old ?? {}),
					id: c.id,
					type: 'cause',
					position: effective(c),
					draggable: !readOnly,
					deletable: false,
					data: {
						cause: c,
						readOnly,
						isEvent: c.id === root.id,
						hasKids: all.some((k) => k.parentId === c.id),
						onPatch: patch,
						onAdd: add,
						onRemove: remove,
					},
				} as CauseNode
			})
		},
		[all, root, readOnly, onChange],
	)

	const [nodes, setNodes] = useState<CauseNode[]>(() => build([]))
	const nodesRef = useRef(nodes)
	nodesRef.current = nodes

	// Sincroniza quando os dados mudam (texto, causa-raiz, novo/removido, reorganizar)
	// biome-ignore lint/correctness/useExhaustiveDependencies: build já depende de `all`
	useEffect(() => {
		setNodes((prev) => build(prev))
	}, [all, readOnly])

	const onNodesChange = useCallback((changes: NodeChange<CauseNode>[]) => {
		setNodes((nds) => applyNodeChanges(changes, nds))
	}, [])

	const edges: Edge[] = all
		.filter((c) => c.parentId)
		.map((c) => {
			const parent = all.find((p) => p.id === c.parentId)
			const siblings = all.filter((s) => s.parentId === c.parentId).length
			return {
				id: `${c.parentId}->${c.id}`,
				source: c.parentId as string,
				target: c.id,
				deletable: false,
				reconnectable: false,
				label:
					siblings > 1 ? (parent?.logic === 'AND' ? 'E' : 'OU') : undefined,
				labelStyle: { fontSize: 10, fontWeight: 700, fill: '#1d6fb8' },
				labelBgPadding: [4, 2],
				labelBgBorderRadius: 8,
				style: { stroke: '#929ea5', strokeWidth: 2 },
				markerEnd: { type: MarkerType.ArrowClosed, color: '#929ea5' },
			}
		})

	/** Só conecta se não criar ciclo e o destino não for o Evento. */
	const validConnection = useCallback(
		(conn: Connection | Edge) => {
			if (!conn.source || !conn.target || conn.source === conn.target)
				return false
			if (conn.target === root.id) return false
			return !descendantsOf(all, conn.target).has(conn.source)
		},
		[all, root.id],
	)

	const onConnect = useCallback(
		(conn: Connection) => {
			if (!validConnection(conn)) return
			// reconecta: o nó de destino passa a ter o novo pai
			onChange(
				all.map((c) =>
					c.id === conn.target ? { ...c, parentId: conn.source } : c,
				),
			)
		},
		[all, onChange, validConnection],
	)

	const persistPositions = useCallback(
		(dragged: Node[]) => {
			const moved = new Map(dragged.map((d) => [d.id, d.position]))
			const current = new Map(nodesRef.current.map((n) => [n.id, n.position]))
			onChange(
				all.map((c) => {
					const p = moved.get(c.id) ?? current.get(c.id)
					return p ? { ...c, x: round(p.x), y: round(p.y) } : c
				}),
			)
		},
		[all, onChange],
	)

	const reorganize = () => {
		const auto = autoLayout(all, root)
		onChange(
			all.map((c) => {
				const p = auto.get(c.id)
				return p ? { ...c, x: p.x, y: p.y } : c
			}),
		)
		setTimeout(() => fitView({ padding: 0.2, duration: 300 }), 50)
	}

	return (
		<div className="h-[620px] overflow-hidden rounded-lg border bg-muted/20">
			<ReactFlow
				nodes={nodes}
				edges={edges}
				nodeTypes={nodeTypes}
				onNodesChange={onNodesChange}
				onNodeDragStop={(_, __, dragged) => persistPositions(dragged)}
				onConnect={onConnect}
				isValidConnection={validConnection}
				nodesDraggable={!readOnly}
				nodesConnectable={!readOnly}
				deleteKeyCode={null}
				fitView
				fitViewOptions={{ padding: 0.2 }}
				minZoom={0.2}
				maxZoom={1.6}
				proOptions={{ hideAttribution: true }}
			>
				<Background variant={BackgroundVariant.Dots} gap={18} size={1.2} />
				<Controls showInteractive={false} />
				<MiniMap pannable zoomable className="!bg-card" />
				{!readOnly && (
					<Panel position="top-right">
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="gap-1.5 bg-card"
							onClick={reorganize}
						>
							<LayoutGrid className="size-4" /> Organizar automaticamente
						</Button>
					</Panel>
				)}
			</ReactFlow>
		</div>
	)
}

/** Canvas livre da Árvore de Causas (React Flow): mover caixas, conectar, E/OU, causa-raiz e minimapa. */
export const CauseGraph = (props: {
	all: Cause[]
	root: Cause
	readOnly: boolean
	onChange: (next: Cause[]) => void
}) => (
	<div className="space-y-2">
		{!props.readOnly && (
			<p className="text-xs text-muted-foreground">
				Arraste as caixas para <b>qualquer posição</b>; use a roda do mouse para
				zoom e arraste o fundo para navegar. Para mudar o pai de uma causa,
				arraste da <b>bolinha da direita</b> de uma caixa até a{' '}
				<b>bolinha da esquerda</b> de outra. “Organizar automaticamente” refaz o
				desenho em árvore.
			</p>
		)}
		<ReactFlowProvider>
			<Canvas {...props} />
		</ReactFlowProvider>
	</div>
)
